import { defineStore } from 'pinia';
import { type Item, itemType, getDefaultItem, stripIrrelevantFieldsForType, weaponType } from '../models/item';
import { type Property } from '../models/property';
import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where, DocumentReference } from 'firebase/firestore';

export interface ItemStateInterface {
	items: Item[];
	selectedItem?: Item;
	loaded: boolean;
	loading: boolean;
}

const SELECTED_ITEM_KEY = 'cybercore_selectedItem';

export const useItemStore = defineStore('itemStore', {
	state: (): ItemStateInterface => ({
		items: [],
		selectedItem: undefined,
		loaded: false,
		loading: false,
	}),

	getters: {
		itemsByType: (state) => {
			return (type: itemType) => state.items.filter((item) => item.type === type);
		},
		weaponsByType: (state) => {
			return (type: weaponType) =>
				state.items.filter((item) => item.type === itemType.weapon && item.weaponType === type);
		},
		weaponsList: (state) => {
			return state.items.filter((item) => item.type === itemType.weapon);
		},
		armourList: (state) => {
			return state.items.filter((item) => item.type === itemType.armour);
		},
		containerList: (state) => {
			return state.items.filter((item) => item.isContainer);
		},
		publicItems: (state): Item[] => {
			// Legacy items (no createdBy) are treated as public.
			return state.items.filter((item) => item.public || !item.createdBy);
		},
		myItems: (state): Item[] => {
			const userStore = useUserStore();
			return state.items.filter((item) => item.createdBy === userStore.user?.uid);
		},
		sortedItems: (state): Item[] => {
			return [...state.items].sort((a, b) => a.name.localeCompare(b.name));
		},
		itemById: (state) => {
			return (itemId: string): Item | undefined =>
				state.items.find((item) => item.item_id === itemId);
		},
	},

	actions: {
		initializeStore() {
			if (process.client) {
				try {
					const saved = localStorage.getItem(SELECTED_ITEM_KEY);
					if (saved) {
						const item = JSON.parse(saved);
						this.selectedItem = item;
						console.log('Restored selected item from localStorage:', item.name);
					}
				} catch (error) {
					console.error('Error restoring selected item from localStorage:', error);
				}
			}
		},

		saveSelectedItemToLocalStorage() {
			if (process.client && this.selectedItem) {
				try {
					// Strip DocumentReferences before serializing
					const serializable = { ...this.selectedItem, property: [] };
					localStorage.setItem(SELECTED_ITEM_KEY, JSON.stringify(serializable));
				} catch (error) {
					console.error('Error saving selected item to localStorage:', error);
				}
			}
		},

		clearSelectedItemFromLocalStorage() {
			if (process.client) {
				try {
					localStorage.removeItem(SELECTED_ITEM_KEY);
				} catch (error) {
					console.error('Error clearing selected item from localStorage:', error);
				}
			}
		},

		async fetchItems() {
			if (this.loaded || this.loading) {
				console.info(`Items already loaded: ${this.items.length}`);
				return;
			}
			const userStore = useUserStore();
			const uid = userStore.user?.uid;
			// Auth hasn't resolved yet — bail WITHOUT marking loaded, so a
			// later call (after login settles) actually runs. Marking loaded
			// here would wedge the store empty for the whole session.
			if (!uid) {
				console.info('fetchItems → skipped (auth not ready)');
				return;
			}
			this.loading = true;
			try {
				console.info('Loading Items');
				console.info('fetchItems → uid:', uid, 'isAdmin:', userStore.isAdmin);
				const { $firestore } = useNuxtApp();
				const colRef = collection($firestore as Firestore, 'item_list');

				// Firestore rule requires per-query constraints that satisfy
				// the read predicate. Admins pass for any doc so the plain
				// getDocs works; non-admins issue two filtered queries
				// (public=true, createdBy=uid) and merge + dedup.
				let docs: any[] = [];
				if (userStore.isAdmin) {
					const snap = await getDocs(colRef);
					docs = snap.docs;
				} else {
					const [pubSnap, ownSnap] = await Promise.all([
						getDocs(query(colRef, where('public', '==', true))),
						getDocs(query(colRef, where('createdBy', '==', uid))),
					]);
					const seen = new Set<string>();
					for (const d of [...pubSnap.docs, ...ownSnap.docs]) {
						if (!seen.has(d.id)) {
							seen.add(d.id);
							docs.push(d);
						}
					}
				}

				console.info(`Loaded Items: ${docs.length}`);
				this.items = docs.map((doc) =>
					({ ...getDefaultItem(), item_id: doc.id, ...doc.data() } as Item)
				);
				this.loaded = true;
			} catch (error) {
				console.error('Error fetching Items: ', error);
			} finally {
				this.loading = false;
			}
		},

		selectItem(itemId: string | undefined) {
			const item = this.items.find((item) => item.item_id === itemId);
			if (item) {
				this.selectedItem = item;
				this.saveSelectedItemToLocalStorage();
			}
		},

		createNewItem(type: itemType = itemType.weapon) {
			const userStore = useUserStore();
			// Pass `type` so weapon-only fields are populated only when
			// creating a weapon (and omitted otherwise) — keeps Firestore
			// docs clean from the start. See models/item.ts:getDefaultItem.
			const newItem = getDefaultItem(type);
			newItem.createdBy = userStore.user?.uid || '';
			newItem.item_id = '-1';
			this.selectedItem = newItem;
			this.saveSelectedItemToLocalStorage();
		},

		updateSelectedItem(updates: Partial<Item>) {
			if (this.selectedItem) {
				this.selectedItem = { ...this.selectedItem, ...updates };
				this.saveSelectedItemToLocalStorage();
			}
		},

		// Whole-object replacement — used by the Raw JSON editor so that
		// fields the user deletes in the textarea actually disappear
		// (the merge in updateSelectedItem would preserve them).
		// Property DocumentReferences are not part of the JSON round-trip,
		// so the caller must hand-merge `property` if it cares.
		replaceSelectedItem(item: Item) {
			this.selectedItem = item;
			this.saveSelectedItemToLocalStorage();
		},

		clearSelectedItem() {
			this.selectedItem = undefined;
			this.clearSelectedItemFromLocalStorage();
		},

		async upsertItem(item: Item): Promise<Item> {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) {
				throw new Error('User must be logged in to save items');
			}

			const { $firestore } = useNuxtApp();
			const nodesRef = collection($firestore as Firestore, 'item_list');
			let nodeRef = doc(nodesRef);

			if (item.item_id === '-1' || item.item_id === undefined) {
				item.item_id = nodeRef.id;
				item.createdBy = userStore.user?.uid || '';
			} else {
				nodeRef = doc(nodesRef, item.item_id);
			}

			try {
				// Defense-in-depth: strip type-irrelevant fields BEFORE the
				// Firestore write. Catches any path that flipped `item.type`
				// after creation (editor type-selector change, programmatic
				// update) where the old type's fields would otherwise leak
				// into the persisted document. Same goal as the periodic
				// `clean-weapon-fields-on-non-weapons.ts` cleanup script —
				// applied at write-time so that script never has to run again.
				const persistable = stripIrrelevantFieldsForType(item);
				await setDoc(nodeRef, persistable);
				this.updateItemInStore(persistable);
				this.selectItem(persistable.item_id);
				return persistable;
			} catch (error) {
				console.error('Error saving Item: ', error);
				throw error;
			}
		},

		async deleteItem(item: Item): Promise<void> {
			const userStore = useUserStore();
			if (!userStore.isLoggedIn) {
				throw new Error('User must be logged in to delete items');
			}

			if (!item.item_id || item.item_id === '-1') return;

			const { $firestore } = useNuxtApp();
			const docRef = doc(collection($firestore as Firestore, 'item_list'), item.item_id);

			try {
				await deleteDoc(docRef);
				this.items = this.items.filter((i) => i.item_id !== item.item_id);
				if (this.selectedItem?.item_id === item.item_id) {
					this.clearSelectedItem();
				}
			} catch (error) {
				console.error('Error deleting Item: ', error);
				throw error;
			}
		},

		updateItemInStore(updatedItem: Item) {
			const index = this.items.findIndex((item) => item.item_id === updatedItem.item_id);
			if (index === -1) {
				this.items.push(updatedItem);
			} else {
				this.items[index] = updatedItem;
			}
		},

		async addPropertyToItem(item: Item, property: Property) {
			if (item.item_id && property.id) {
				const { $firestore } = useNuxtApp();
				const itemRef = doc($firestore as Firestore, `item_list/${item.item_id}`);

				if (item.property.some((ref: any) => ref.id === property.id)) {
					console.log('Property already exists in the item');
					return;
				}

				const propertyRef = property.ref ?? doc($firestore as Firestore, `/properties/${property.id}`);
				const updatedPropertyRefs = [...item.property, propertyRef];
				const updatedItem = { ...item, property: updatedPropertyRefs };

				try {
					await setDoc(itemRef, updatedItem);
					this.updateItemInStore(updatedItem);
					if (this.selectedItem?.item_id === item.item_id) {
						this.selectedItem = { ...updatedItem };
					}
				} catch (error) {
					console.error('Error adding property to item: ', error);
				}
			}
		},

		async removePropertyFromItem(item: Item, property: Property) {
			if (item.item_id && property.id) {
				const { $firestore } = useNuxtApp();
				const itemRef = doc($firestore as Firestore, `item_list/${item.item_id}`);

				const updatedPropertyRefs = item.property.filter((ref: any) => ref.id !== property.id);
				const updatedItem = { ...item, property: updatedPropertyRefs };

				try {
					await setDoc(itemRef, updatedItem);
					this.updateItemInStore(updatedItem);
					if (this.selectedItem?.item_id === item.item_id) {
						this.selectedItem = { ...updatedItem };
					}
				} catch (error) {
					console.error('Error removing property from item: ', error);
				}
			}
		},
	},
});
